import React, { useEffect, useMemo, useState, useRef } from "react";
import Papa from "papaparse";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import {
  MessageSquare,
  Globe,
  Upload,
  Search,
  BarChart3,
  Lightbulb,
  ExternalLink,
  Target,
  TrendingUp,
  Filter,
  X,
  ListFilter,
  ArrowUpDown,
  RefreshCcw,
  Layers,
} from "lucide-react";

// --- THEME ---
const THEME = {
  bg: "#0f172a", // Deep Space
  sidebar: "#1e293b", // Panel Grey
  card: "#1e293b",
  border: "#334155",
  textMain: "#f8fafc",
  textMuted: "#94a3b8",
  accent: "#f43f5e", // Red/Pink
  strategy: "#8b5cf6", // Purple
  market: "#10b981", // Green
  brand: "#3b82f6", // Blue
  warning: "#f59e0b", // Amber
};

// --- PRE-LOADED DATA ---
const INITIAL_DATA = [
  {
    Title: "Why our pipeline conversion rates are dropping",
    Snippet:
      "We have plenty of MQLs but the MQL to SQL conversion is abysmal. Sales says lead quality is low...",
    Platform: "LinkedIn",
    Pillars: "Lead Gen & Pipeline",
    Themes: "Pain Point",
    SignalTypes: "Pain Point",
    PostDate: "2025-02-15",
    PostURL: "#",
  },
  {
    Title: "Evaluating 6sense vs Demandbase for Intent Data",
    Snippet:
      "Our team is split. We need better predictive scoring but the data coverage varies wildly...",
    Platform: "LinkedIn",
    Pillars: "Intent & Scoring",
    Themes: "Evaluation",
    SignalTypes: "Tool Comparison",
    PostDate: "2025-02-14",
    PostURL: "#",
  },
  {
    Title: "The state of dirty CRM data in 2025",
    Snippet:
      "I spent all weekend trying to stitch contact records. Why is enrichment still so hard?",
    Platform: "LinkedIn",
    Pillars: "Data Quality",
    Themes: "Observation",
    SignalTypes: "Market Observation",
    PostDate: "2025-02-14",
    PostURL: "#",
  },
  {
    Title: "Looking for a new Head of RevOps",
    Snippet:
      "We need someone to fix our Salesforce instance and implement a real attribution model.",
    Platform: "LinkedIn",
    Pillars: "Lead Gen & Pipeline",
    Themes: "Hiring",
    SignalTypes: "Hiring / Jobs",
    PostDate: "2025-02-12",
    PostURL: "#",
  },
  {
    Title: "Predictive Analytics for Churn Prevention",
    Snippet:
      "We are testing a new ML model to flag at-risk accounts before renewal...",
    Platform: "LinkedIn",
    Pillars: "AI & Modeling",
    Themes: "Thought Leadership",
    SignalTypes: "Thought Leadership",
    PostDate: "2025-02-10",
    PostURL: "#",
  },
];

// --- COMPONENTS ---

function FilterToolbar({
  filters,
  setFilters,
  options,
  totalResults,
  onReset,
}) {
  const handleChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div
      style={{
        background: THEME.card,
        padding: "12px 16px",
        borderBottom: `1px solid ${THEME.border}`,
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      {/* Search */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: THEME.bg,
          border: `1px solid ${THEME.border}`,
          borderRadius: 8,
          padding: "6px 10px",
          flex: 1,
          minWidth: 200,
          maxWidth: 400,
        }}
      >
        <Search size={14} color={THEME.textMuted} style={{ marginRight: 8 }} />
        <input
          type="text"
          placeholder="Search signals..."
          value={filters.search}
          onChange={(e) => handleChange("search", e.target.value)}
          style={{
            background: "transparent",
            border: "none",
            color: THEME.textMain,
            outline: "none",
            width: "100%",
            fontSize: 13,
          }}
        />
        {filters.search && (
          <X
            size={14}
            style={{ cursor: "pointer" }}
            onClick={() => handleChange("search", "")}
          />
        )}
      </div>

      {/* Dropdowns */}
      <select
        value={filters.pillar}
        onChange={(e) => handleChange("pillar", e.target.value)}
        style={{
          background: THEME.bg,
          color: THEME.textMain,
          border: `1px solid ${THEME.border}`,
          padding: "6px 10px",
          borderRadius: 8,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <option value="All">All Topics</option>
        {options.pillars.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>

      <select
        value={filters.platform}
        onChange={(e) => handleChange("platform", e.target.value)}
        style={{
          background: THEME.bg,
          color: THEME.textMain,
          border: `1px solid ${THEME.border}`,
          padding: "6px 10px",
          borderRadius: 8,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <option value="All">All Platforms</option>
        {options.platforms.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>

      {/* Sort */}
      <button
        onClick={() =>
          handleChange("sort", filters.sort === "newest" ? "oldest" : "newest")
        }
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: THEME.bg,
          border: `1px solid ${THEME.border}`,
          color: THEME.textMuted,
          padding: "6px 10px",
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        <ArrowUpDown size={14} />
        {filters.sort === "newest" ? "Newest" : "Oldest"}
      </button>

      {/* Results Count & Reset */}
      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 12, color: THEME.textMuted }}>
          {totalResults} matches
        </div>
        <button
          onClick={onReset}
          title="Reset Filters"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: THEME.textMuted,
          }}
        >
          <RefreshCcw size={14} />
        </button>
      </div>
    </div>
  );
}

function FeedItem({ item, onTagClick }) {
  const postUrl = item.PostURL || item.link || "#";

  const getBadgeColor = (type) => {
    const t = (type || "").toLowerCase();
    if (t.includes("pain") || t.includes("problem")) return THEME.accent;
    if (t.includes("hiring")) return THEME.warning;
    if (t.includes("intent") || t.includes("buy") || t.includes("compare"))
      return THEME.market;
    return THEME.strategy;
  };

  const badgeColor = getBadgeColor(item.SignalTypes);

  return (
    <div
      style={{
        padding: "16px 20px",
        borderBottom: `1px solid ${THEME.border}`,
        transition: "background 0.2s",
      }}
      className="feed-item"
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <div style={{ display: "flex", gap: 8 }}>
          <span
            onClick={() => onTagClick && onTagClick("platform", item.Platform)}
            style={{
              fontSize: 10,
              fontWeight: 800,
              padding: "2px 6px",
              borderRadius: 4,
              background: `${THEME.textMuted}20`,
              color: THEME.textMuted,
              cursor: "pointer",
            }}
          >
            {item.Platform || "Web"}
          </span>
          <span
            onClick={() =>
              onTagClick &&
              onTagClick("signalType", item.SignalTypes?.split("|")[0])
            }
            style={{
              fontSize: 10,
              fontWeight: 800,
              padding: "2px 6px",
              borderRadius: 4,
              background: `${badgeColor}20`,
              color: badgeColor,
              cursor: "pointer",
            }}
          >
            {item.SignalTypes?.split("|")[0] || "Signal"}
          </span>
        </div>
        <span style={{ fontSize: 11, color: THEME.textMuted }}>
          {item.PostDate}
        </span>
      </div>

      <a
        href={postUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", display: "block" }}
      >
        <div
          style={{
            fontSize: 14,
            lineHeight: 1.4,
            color: THEME.textMain,
            fontWeight: 600,
            marginBottom: 4,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {item.Title}
          <ExternalLink size={12} color={THEME.textMuted} />
        </div>
      </a>

      <div
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          color: THEME.textMain,
          opacity: 0.8,
        }}
      >
        "{item.Snippet}"
      </div>

      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {item.Pillars &&
          item.Pillars.split("|")
            .slice(0, 3)
            .map((p, i) => (
              <span
                key={i}
                onClick={() => onTagClick && onTagClick("pillar", p.trim())}
                style={{
                  fontSize: 10,
                  color: THEME.brand,
                  background: `${THEME.brand}10`,
                  padding: "2px 6px",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                {p.trim()}
              </span>
            ))}
      </div>
    </div>
  );
}

function StatCard({ title, value, sub, color, icon }) {
  return (
    <div
      style={{
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: 12,
        padding: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: THEME.textMuted,
              textTransform: "uppercase",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: THEME.textMain,
              marginTop: 4,
            }}
          >
            {value}
          </div>
          <div style={{ fontSize: 12, color: color, marginTop: 4 }}>{sub}</div>
        </div>
        <div style={{ color: color, opacity: 0.8 }}>{icon}</div>
      </div>
    </div>
  );
}

// --- MAIN APPLICATION ---

export default function App() {
  const [rows, setRows] = useState(INITIAL_DATA);
  const [view, setView] = useState("EXPLORE");
  const [status, setStatus] = useState("Demo Data");
  const fileInputRef = useRef(null);

  // Global Filters
  const [filters, setFilters] = useState({
    search: "",
    pillar: "All",
    platform: "All",
    signalType: "All",
    sort: "newest",
  });

  // Load Data
  useEffect(() => {
    fetch("/fairway_market_signals.csv")
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.text();
      })
      .then((csv) => {
        Papa.parse(csv, {
          header: true,
          skipEmptyLines: true,
          complete: (res) => {
            const clean = (res.data || []).filter(
              (r) => r.Snippet && r.Snippet.length > 5
            );
            if (clean.length) {
              setRows(clean);
              setStatus("Live Data");
            }
          },
        });
      })
      .catch(() => console.log("Using Demo Data"));
  }, []);

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (res) => {
          const clean = (res.data || []).filter(
            (r) => r.Snippet && r.Snippet.length > 5
          );
          if (clean.length) {
            setRows(clean);
            setStatus("Uploaded Data");
          }
        },
      });
    }
  };

  // --- CORE LOGIC: FILTER & ANALYZE ---
  const { filteredRows, analytics, filterOptions } = useMemo(() => {
    // 1. Extract Unique Options (from full dataset)
    const pillars = new Set();
    const platforms = new Set();
    const signalTypes = new Set();
    rows.forEach((r) => {
      if (r.Platform) platforms.add(r.Platform);
      if (r.Pillars) r.Pillars.split("|").forEach((p) => pillars.add(p.trim()));
      if (r.SignalTypes)
        r.SignalTypes.split("|").forEach((s) => signalTypes.add(s.trim()));
    });

    // 2. Filter Rows
    let result = rows.filter((r) => {
      const matchesSearch =
        !filters.search ||
        (r.Title + r.Snippet)
          .toLowerCase()
          .includes(filters.search.toLowerCase());
      const matchesPillar =
        filters.pillar === "All" || (r.Pillars || "").includes(filters.pillar);
      const matchesPlatform =
        filters.platform === "All" || r.Platform === filters.platform;
      // const matchesSignal = filters.signalType === "All" || (r.SignalTypes || "").includes(filters.signalType);
      // Note: We might want SignalType filter in the View itself, but global is better for "No Bull"
      return matchesSearch && matchesPillar && matchesPlatform;
    });

    // 3. Sort
    result.sort((a, b) => {
      const dA = new Date(a.PostDate || 0);
      const dB = new Date(b.PostDate || 0);
      return filters.sort === "newest" ? dB - dA : dA - dB;
    });

    // 4. Group for Analytics (derived from FILTERED rows)
    const strategy = [];
    const market = [];
    result.forEach((r) => {
      const type = (r.SignalTypes || "").toLowerCase();
      if (
        type.includes("intent") ||
        type.includes("comparison") ||
        type.includes("pain") ||
        type.includes("problem") ||
        type.includes("pricing")
      ) {
        market.push(r);
      } else {
        strategy.push(r);
      }
    });

    const pillarCounts = {};
    result.forEach((r) => {
      if (!r.Pillars) return;
      r.Pillars.split("|").forEach((p) => {
        const clean = p.trim();
        if (clean && clean !== "Unspecified")
          pillarCounts[clean] = (pillarCounts[clean] || 0) + 1;
      });
    });
    const pillarData = Object.keys(pillarCounts)
      .map((k) => ({ name: k, value: pillarCounts[k] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    const timeline = {};
    result.forEach((r) => {
      const d = r.PostDate ? r.PostDate.substring(0, 7) : "Unknown";
      if (d.length >= 7) timeline[d] = (timeline[d] || 0) + 1;
    });
    const timelineData = Object.keys(timeline)
      .sort()
      .map((d) => ({ date: d, count: timeline[d] }));

    const platformCounts = {};
    result.forEach((r) => {
      platformCounts[r.Platform || "Web"] =
        (platformCounts[r.Platform || "Web"] || 0) + 1;
    });
    const platformData = Object.keys(platformCounts).map((k) => ({
      name: k,
      value: platformCounts[k],
    }));

    return {
      filteredRows: result,
      analytics: { strategy, market, pillarData, timelineData, platformData },
      filterOptions: {
        pillars: Array.from(pillars).sort(),
        platforms: Array.from(platforms).sort(),
        signalTypes: Array.from(signalTypes).sort(),
      },
    };
  }, [rows, filters]);

  const handleTagClick = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // --- VIEWS ---

  const renderExplorer = () => (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {filteredRows.length === 0 ? (
        <div
          style={{ padding: 40, textAlign: "center", color: THEME.textMuted }}
        >
          No signals match your filters.
        </div>
      ) : (
        <div style={{ overflowY: "auto", flex: 1 }}>
          {filteredRows.map((item, i) => (
            <FeedItem key={i} item={item} onTagClick={handleTagClick} />
          ))}
        </div>
      )}
    </div>
  );

  const renderExec = () => (
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
          }}
        >
          <StatCard
            title="Total Signals"
            value={filteredRows.length}
            sub="Filtered View"
            color={THEME.accent}
            icon={<MessageSquare />}
          />
          <StatCard
            title="Buying Intent"
            value={analytics.market.length}
            sub="Filtered Intent"
            color={THEME.market}
            icon={<Target />}
          />
          <StatCard
            title="General Market"
            value={analytics.strategy.length}
            sub="Filtered Noise"
            color={THEME.strategy}
            icon={<TrendingUp />}
          />
        </div>
        <div
          style={{
            background: THEME.card,
            border: `1px solid ${THEME.border}`,
            borderRadius: 12,
            padding: 24,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>
            Volume Trend (Filtered)
          </div>
          <div style={{ height: 250 }}>
            <ResponsiveContainer>
              <AreaChart data={analytics.timelineData}>
                <defs>
                  <linearGradient id="colorVol" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={THEME.accent}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={THEME.accent}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#334155"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: THEME.textMuted }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: THEME.textMuted }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: THEME.card,
                    borderColor: THEME.border,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={THEME.accent}
                  fill="url(#colorVol)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div
          style={{
            background: THEME.card,
            border: `1px solid ${THEME.border}`,
            borderRadius: 12,
            padding: 20,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 12 }}>
            Platform Breakdown
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={analytics.platformData}
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {analytics.platformData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        [
                          THEME.accent,
                          THEME.brand,
                          THEME.market,
                          THEME.strategy,
                        ][index % 4]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: THEME.card,
                    borderColor: THEME.border,
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={{ fontSize: 11 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStrategy = () => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 24 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div
          style={{
            background: THEME.card,
            border: `1px solid ${THEME.border}`,
            borderRadius: 12,
            padding: 24,
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 8,
              color: THEME.strategy,
            }}
          >
            Strategy Signals
          </div>
          <div style={{ fontSize: 36, fontWeight: 800, color: THEME.textMain }}>
            {analytics.strategy.length}
          </div>
          <div style={{ fontSize: 12, color: THEME.textMuted }}>
            Matches in current filters
          </div>

          <div
            style={{
              marginTop: 24,
              paddingTop: 24,
              borderTop: `1px solid ${THEME.border}`,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: THEME.textMuted,
                marginBottom: 12,
              }}
            >
              TOP FILTERED TOPICS
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {analytics.pillarData.slice(0, 5).map((s) => (
                <span
                  key={s.name}
                  onClick={() => handleTagClick("pillar", s.name)}
                  style={{
                    fontSize: 11,
                    background: `${THEME.strategy}20`,
                    color: THEME.strategy,
                    padding: "4px 8px",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  {s.name} ({s.value})
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          background: THEME.card,
          border: `1px solid ${THEME.border}`,
          borderRadius: 12,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${THEME.border}`,
            fontWeight: 700,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Lightbulb size={16} color={THEME.strategy} /> Strategy Feed
          (Filtered)
        </div>
        <div style={{ overflowY: "auto", flex: 1, maxHeight: 600 }}>
          {analytics.strategy.length === 0 ? (
            <div style={{ padding: 20, color: THEME.textMuted }}>
              No strategy signals match filters.
            </div>
          ) : (
            analytics.strategy.map((item, i) => (
              <FeedItem key={i} item={item} onTagClick={handleTagClick} />
            ))
          )}
        </div>
      </div>
    </div>
  );

  const renderMarket = () => (
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
      <div
        style={{
          background: THEME.card,
          border: `1px solid ${THEME.border}`,
          borderRadius: 12,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${THEME.border}`,
            fontWeight: 700,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Search size={16} color={THEME.market} /> Market Intel (Filtered)
        </div>
        <div style={{ overflowY: "auto", flex: 1, maxHeight: 600 }}>
          {analytics.market.length === 0 ? (
            <div style={{ padding: 20, color: THEME.textMuted }}>
              No intent signals match filters.
            </div>
          ) : (
            analytics.market.map((item, i) => (
              <FeedItem key={i} item={item} onTagClick={handleTagClick} />
            ))
          )}
        </div>
      </div>
      <div>
        <div
          style={{
            background: THEME.card,
            border: `1px solid ${THEME.border}`,
            borderRadius: 12,
            padding: 24,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>
            Active Discussions
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {analytics.pillarData.map((k) => (
              <span
                key={k.name}
                onClick={() => handleTagClick("pillar", k.name)}
                style={{
                  fontSize: 12,
                  padding: "6px 12px",
                  background: THEME.bg,
                  border: `1px solid ${THEME.border}`,
                  borderRadius: 20,
                  color: THEME.textMain,
                  cursor: "pointer",
                }}
              >
                {k.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        background: THEME.bg,
        color: THEME.textMain,
        fontFamily: "Inter, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: 260,
          background: THEME.sidebar,
          borderRight: `1px solid ${THEME.border}`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: 24,
            fontSize: 18,
            fontWeight: 900,
            color: THEME.textMain,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              background: THEME.accent,
              width: 28,
              height: 28,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            F
          </div>
          FAIRWAY
        </div>
        <div style={{ flex: 1, padding: "0 12px" }}>
          {[
            {
              id: "EXPLORE",
              label: "Signal Explorer",
              icon: <ListFilter size={18} />,
            },
            { id: "EXEC", label: "Executive Pulse", icon: <Globe size={18} /> },
            {
              id: "STRATEGY",
              label: "Strategic Signals",
              icon: <Lightbulb size={18} />,
            },
            { id: "MARKET", label: "Market Intel", icon: <Search size={18} /> },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                background:
                  view === item.id ? `${THEME.accent}20` : "transparent",
                color: view === item.id ? THEME.accent : THEME.textMuted,
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                marginBottom: 4,
                fontWeight: view === item.id ? 700 : 500,
                textAlign: "left",
                transition: "all 0.2s",
              }}
            >
              {item.icon} {item.label}
            </button>
          ))}
        </div>
        <div style={{ padding: 12 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            style={{ display: "none" }}
            accept=".csv"
          />
          <button
            onClick={() => fileInputRef.current.click()}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "10px",
              background: "transparent",
              border: `1px dashed ${THEME.textMuted}`,
              borderRadius: 8,
              color: THEME.textMuted,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            <Upload size={14} /> Upload Data
          </button>
        </div>
        <div
          style={{
            padding: 24,
            borderTop: `1px solid ${THEME.border}`,
            fontSize: 11,
            color: THEME.textMuted,
          }}
        >
          {status} • {rows.length} Total
        </div>
      </div>

      {/* Main Content with Global Filter Bar */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Global Filter Toolbar */}
        <FilterToolbar
          filters={filters}
          setFilters={setFilters}
          options={filterOptions}
          totalResults={filteredRows.length}
          onReset={() =>
            setFilters({
              search: "",
              pillar: "All",
              platform: "All",
              signalType: "All",
              sort: "newest",
            })
          }
        />

        <div style={{ padding: 32, flex: 1, overflowY: "auto" }}>
          {view === "EXPLORE" && renderExplorer()}
          {view === "EXEC" && renderExec()}
          {view === "STRATEGY" && renderStrategy()}
          {view === "MARKET" && renderMarket()}
        </div>
      </div>
    </div>
  );
}
